import { useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { Button } from '@/shared/ui/Button';
import {
  OUTCOME,
  TRIGGER_LABEL,
  costText,
  durationText,
  fixedInStep,
  plainRunError,
  relativeTime,
  stepText,
  tokensText,
} from '@/modules/agents/utils/agentStatusLabels';
import type { AgentRunLogEntry } from '../../api/agentStatusTypes';
import { useAgentRunLog } from '../../hooks/useCommandCenter';
import { TONE_CLASS } from './tone';
import styles from './AgentStatus.module.css';

/**
 * Every run that did or tried something for one agent at one location, newest first, 30 days.
 * Idle wakes are not here; the status line shows the last one. `focusRunId` opens that run and
 * scrolls to it (the run a "Needs attention" reason or a task's timeline pointed at).
 */
export function RunLog({
  practiceId,
  locationId,
  roleKey,
  focusRunId,
  agentName,
}: {
  practiceId: string;
  locationId: string;
  roleKey: string;
  focusRunId?: string;
  /** The agent's name for the plain-language error lines ("Jenny ran out of steps…"). */
  agentName?: string;
}) {
  const log = useAgentRunLog(practiceId, locationId, roleKey);
  const entries = log.data?.pages.flatMap((page) => page.entries) ?? [];
  const now = Date.now();

  if (log.isLoading) return <p className={styles.quiet}>Loading the run log…</p>;
  if (log.isError) return <p className={styles.quiet}>The run log could not be loaded.</p>;

  return (
    <div className={styles.runLog}>
      <div className={styles.runLogHead}>
        <span>Runs that did or tried something, newest first. Kept 30 days.</span>
      </div>
      {entries.length === 0 && <p className={styles.quiet}>No runs in the last 30 days.</p>}
      {entries.map((entry) => (
        <RunEntry
          key={entry.id}
          entry={entry}
          now={now}
          focused={entry.id === focusRunId}
          agentName={agentName}
        />
      ))}
      {log.hasNextPage && (
        <div className={styles.loadMore}>
          <Button
            variant="secondary"
            size="sm"
            disabled={log.isFetchingNextPage}
            onClick={() => log.fetchNextPage()}
          >
            {log.isFetchingNextPage ? 'Loading…' : 'Load older runs'}
          </Button>
        </div>
      )}
    </div>
  );
}

function RunEntry({
  entry,
  now,
  focused,
  agentName,
}: {
  entry: AgentRunLogEntry;
  now: number;
  focused: boolean;
  agentName?: string;
}) {
  const [open, setOpen] = useState(focused);
  const ref = useRef<HTMLDivElement>(null);
  const outcome = OUTCOME[entry.outcome];
  const nothingToDo = entry.outcome === 'nothing_to_do';
  const draftSaved = entry.steps.some((step) => step.kind === 'draft' && step.outcome === 'saved');

  useEffect(() => {
    if (!focused) return;
    setOpen(true);
    ref.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [focused]);

  return (
    <div ref={ref} className={clsx(styles.run, focused && styles.runFocused)}>
      <button
        type="button"
        className={styles.runSummary}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span className={styles.runLine}>
          <span className={TONE_CLASS[outcome.tone]}>{outcome.label}</span>
          {/* A run with no task failed before it chose one (or had nothing to do). */}
          <span className={styles.runTask}>{entry.taskTitle ?? 'No task picked'}</span>
        </span>
        <span className={styles.runLine}>
          <span title={new Date(entry.startedAt).toLocaleString()}>
            {relativeTime(entry.startedAt, now)}
          </span>
          <span>· Started by {TRIGGER_LABEL[entry.trigger]}</span>
          <span>· {durationText(entry.finishedAt - entry.startedAt)}</span>
          <span>· {costText(entry.costUsd)}</span>
          <span>
            · {entry.steps.length} step{entry.steps.length === 1 ? '' : 's'}
          </span>
        </span>
        {/* A run can span several log entries: one per wake that worked it (it may wait and
            resume). A wake that failed before creating a run has no run id. */}
        <span className={styles.mono}>
          log {entry.id}
          {entry.runId ? ` · run ${entry.runId}` : ' · no run'}
        </span>
        {/* A Run now that found nothing: `error` holds why, in words, not a failure. */}
        {!open && entry.error && nothingToDo && <span className={styles.quiet}>{entry.error}</span>}
        {!open && entry.error && !nothingToDo && (
          <span className={styles.cellError}>
            {plainRunError(entry.error, agentName, draftSaved)}
          </span>
        )}
      </button>
      {open && (
        <div className={styles.runDetail}>
          {entry.error && nothingToDo && (
            <>
              <span className={styles.detailLabel}>Why</span>
              <p className={styles.quiet}>{entry.error}</p>
            </>
          )}
          {entry.error && !nothingToDo && (
            <>
              <span className={styles.detailLabel}>What went wrong</span>
              <p className={styles.cellError}>
                {plainRunError(entry.error, agentName, draftSaved)}
              </p>
              {/* The engine's own message, for whoever debugs it: kept out of every summary line. */}
              <details>
                <summary className={styles.detailLabel}>Technical details</summary>
                <pre className={styles.errorBlock}>{entry.error}</pre>
              </details>
            </>
          )}
          <span className={styles.detailLabel}>Steps</span>
          {entry.steps.length === 0 ? (
            <p className={styles.quiet}>No steps recorded.</p>
          ) : (
            <ol className={styles.steps}>
              {entry.steps.map((step, index) => {
                // A refusal a later step fixed is how the checks work, not a failure: shown plainly, not in warning colour.
                const fixedIn = fixedInStep(entry.steps, index);
                return (
                  <li
                    key={index}
                    className={clsx(
                      step.outcome === 'refused' &&
                        (fixedIn ? styles.stepFixed : styles.stepRefused),
                      step.outcome === 'failed' && styles.stepFailed,
                    )}
                  >
                    {/* A failed step saves no note: it ended the run, so the run's error says why. */}
                    {stepText(
                      step.outcome === 'failed' && !step.note && entry.error
                        ? { ...step, note: entry.error }
                        : step,
                    )}
                    {fixedIn && (
                      <span className={styles.stepFixedNote}>
                        {' '}
                        · caught and fixed in step {fixedIn}
                      </span>
                    )}
                    {(step.inputTokens !== undefined || step.costUsd > 0) && (
                      <span className={styles.stepUsage}>
                        {tokensText(step)} · {costText(step.costUsd)}
                      </span>
                    )}
                    {/* The engine's own message to the agent, for whoever debugs the run. */}
                    {step.outcome !== 'saved' && step.note && (
                      <details>
                        <summary className={styles.detailLabel}>Technical details</summary>
                        <pre className={styles.errorBlock}>{step.note}</pre>
                      </details>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
          {entry.rawReply && (
            <>
              <span className={styles.detailLabel}>What the model replied</span>
              <pre className={styles.rawBlock}>{entry.rawReply}</pre>
            </>
          )}
          <span className={styles.mono}>{tokensText(entry)} tokens</span>
        </div>
      )}
    </div>
  );
}
